import React, { MouseEventHandler } from 'react'
import classNames from 'classnames'

import { Button, Heading, Loader } from '@foxino/components-web'
import { ArrowCircleBrokenDown } from '@foxino/icons-react'

import { useTranslation } from '@app/locales'

type SizeType = 'lg' | 'md' | 'sm' | 'xs'

type Props = Omit<React.HTMLProps<HTMLDivElement>, 'size'> &
  React.PropsWithChildren & {
    size?: SizeType
    showUnderOpacity?: boolean
    showChildrenOnLoading?: boolean
    loading: boolean
    loader?: React.ReactNode
    error?: any
    refetch?: () => void
  }

/**
 * @group Components
 * @category Props
 */
export const LoaderWrapper = ({
  size = 'lg',
  showUnderOpacity,
  showChildrenOnLoading = false,
  loading = true,
  children,
  error,
  refetch,
  className,
  loader,
  ...restProps
}: Props) => {
  const { t } = useTranslation('common')

  if ((!loading || showChildrenOnLoading) && !error) {
    return children
  }

  const handleOnClick: MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation()
  }

  return (
    <div
      onClick={handleOnClick}
      {...restProps}
      className={classNames('flex h-full w-full flex-row items-center justify-center')}
    >
      {error ? (
        <div className="flex w-full items-center justify-center rounded-xl bg-gradient-to-tr from-error-400/40 to-error-500 p-4 shadow-xl backdrop-blur-lg">
          <div className="flex flex-col items-center">
            <Heading size="lg">{t('errorTitle')}</Heading>
            <Heading size="md">{t('errorGlobal')}</Heading>
          </div>

          {refetch && (
            <Button loading={loading} LeadingIcon={ArrowCircleBrokenDown} color="secondary" onClick={() => refetch()}>
              {t('refetch')}
            </Button>
          )}
        </div>
      ) : (
        <div className="relative h-full w-full">
          <div
            className={classNames('h-full w-full', showUnderOpacity === true ? 'opacity-20' : 'opacity-100', className)}
          >
            {loading ? loader : children}
            {showUnderOpacity === true && children}
          </div>
          {!loader && (
            <div className="absolute right-[calc(50%_-_1.5rem)] top-[40%]">
              <Loader size={size} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
